import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../../shared/makeApiCall";

import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";

export function ProductDetailsScreenViewModel(logout, productId, addSnackbar) {
  const [loading, setLoading] = useState(true);

  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [inventoryTypeOptions, setInventoryTypeOptions] = useState([]);
  const [classTypeOptions, setClassTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [boxTypeOptions, setBoxTypeOptions] = useState([]);
  const [allergenOptions, setAllergenOptions] = useState([]);
  const [graphicsOptions, setGraphicsOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [warehouseLocationsOptions, setWarehouseLocationsOptions] = useState(
    [],
  );
  const [product, setProduct] = useState(null);
  const [productColours, setProductColours] = useState([]);
  const [productOdours, setProductOdours] = useState([]);
  const [productTastes, setProductTastes] = useState([]);
  const [vendorSKUs, setVendorSKUs] = useState([]);
  const [vendorSKUsLoading, setVendorSKUsLoading] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [createVendorSkuInProgress, setCreateVendorSkuInProgress] =
    useState(false);
  const [updateVendorSkuInProgress, setUpdateVendorSkuInProgress] =
    useState(false);
  const [productDuplicates, setProductDuplicates] = useState([]);

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getInventoryTypes = async function () {
    let callResult = await makeGetApiCall("inventoryTypesForDropdown");
    if (callResult.success) {
      setInventoryTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch inventory types");
    }
  };

  const getClassTypes = async function () {
    let callResult = await makeGetApiCall("classesForDropdown");
    if (callResult.success) {
      setClassTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch class types");
    }
  };

  const getCategoryTypes = async function () {
    let callResult = await makeGetApiCall("categoriesForDropdown");
    if (callResult.success) {
      setCategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category types");
    }
  };

  const getBoxTypes = async function () {
    let callResult = await makeGetApiCall("boxTypesForDropdown");
    if (callResult.success) {
      setBoxTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch box types");
    }
  };

  const getAllergens = async function () {
    let callResult = await makeGetApiCall("allergensForDropdown");
    if (callResult.success) {
      setAllergenOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch allergens");
    }
  };

  const getWarehouses = async function () {
    let callResult = await makeGetApiCall("warehousesForDropdown");
    if (callResult.success) {
      setWarehouseOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch warehouses");
    }
  };

  const getWarehouseLocations = async function (warehouseId) {
    let callResult = await makeGetApiCall(
      `warehouseLocationsForDropdown/${warehouseId}`,
    );
    if (callResult.success) {
      setWarehouseLocationsOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch warehouse locations");
    }
  };

  const getProductDuplicates = async function (productId) {
    let callResult = await makeGetApiCall(`/productDuplicates/${productId}`);

    if (callResult.success) {
      const product = callResult.data;

      setProductDuplicates(product);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not get product duplicates");
    }
  };

  const updateProductDuplicate = async function (id, new_status) {
    let callResult = await makePostApiCall("updateProductDuplicate", {
      id,
      new_status,
    });

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message ?? "Product duplicate updated",
          SnackbarType.Success
        )
      );
      if (product?.plu) {
        getProductDuplicates(product.id);
      }
      if (new_status === 1) {
        getProductDetails();
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          callResult.data?.message ?? "Could not update product duplicate"
        );
      }
    }
    return callResult;
  };

  const getGraphics = async function (warehouseId) {
    setLoading(true);
    let callResult = await makeGetApiCall(`graphicsForDropdown`);
    setLoading(false);
    if (callResult.success) {
      setGraphicsOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch graphics");
    }
  };

  const getProductDetails = async function () {
    setLoading(true);
    let callResult = await makeGetApiCall(`products/${productId}`);

    if (callResult.success) {
      const product = callResult.data;
      if (product.warehouse_location_id > 0) {
        await getWarehouseLocations(product.warehouse_id);
      }

      setProduct(product);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not update product details");
    }
    setLoading(false);
  };

  const updateProduct = async function (values) {
    const payload = { ...values };
    let callResult = await makePostApiCall(
      `products/${productId}/update`,
      payload,
      "multipart/form-data",
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Product updated",
          SnackbarType.Success,
        ),
      );
      setProduct(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const onDuplicate = async function () {
    let callResult = await makePostApiCall(`products/duplicate/${productId}`);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Product duplicated",
          SnackbarType.Success,
        ),
      );
      window.location = "/products/" + callResult.data.id;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not duplicate product");
    }
  };

  const getProductColours = async function () {
    let callResult = await makeGetApiCall("productColoursForDropdown");
    if (callResult.success) {
      setProductColours(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch inventory types");
    }
  };

  const getProductOdours = async function () {
    let callResult = await makeGetApiCall("productOdoursForDropdown");
    if (callResult.success) {
      setProductOdours(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch inventory types");
    }
  };

  const getProductTastes = async function () {
    let callResult = await makeGetApiCall("productTastesForDropdown");
    if (callResult.success) {
      setProductTastes(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch inventory types");
    }
  };

  const getProductVendorSkus = async function (search = null) {
    setVendorSKUsLoading(true);
    const payload = search ? { search } : {};
    let callResult = await makePostApiCall(
      `productVendorSkus/${productId}`,
      payload,
    );
    setVendorSKUsLoading(false);
    if (callResult.success) {
      setVendorSKUs(callResult.data.productVendorSkus);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch vendor SKUs");
    }
  };

  const getSuppliersForDropdown = async function () {
    let callResult = await makeGetApiCall("suppliersForDropdown");
    if (callResult.success) {
      setSupplierOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers");
    }
  };

  const createVendorSku = async function (values) {
    setCreateVendorSkuInProgress(true);
    const payload = {
      product_id: productId,
      supplier_id: values.supplier_id.value,
      vendor_sku: values.vendor_sku || null,
    };
    let callResult = await makePostApiCall(`productVendorSkus/create`, payload);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Vendor SKU created",
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      await getProductVendorSkus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        "Could not create vendor SKU",
        callResult.data?.message ||
          "Please make sure all required fields are filled.",
      );
    }
    setCreateVendorSkuInProgress(false);
  };

  const updateVendorSku = async function (vendorSkuId, values) {
    setUpdateVendorSkuInProgress(true);
    const payload = {
      vendor_sku: values.vendor_sku || null,
    };
    let callResult = await makePutApiCall(
      `productVendorSkus/${vendorSkuId}`,
      payload,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Vendor SKU updated",
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      await getProductVendorSkus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        "Could not update vendor SKU",
        callResult.data?.message ||
          "Please make sure all required fields are filled.",
      );
    }
    setUpdateVendorSkuInProgress(false);
  };

  const deleteVendorSku = async function (vendorSkuId) {
    let callResult = await makePostApiCall(
      `deleteProductVendorSku/${vendorSkuId}`,
      {},
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Vendor SKU deleted",
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      await getProductVendorSkus();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        "Could not delete vendor SKU",
        callResult.data?.message || "An error occurred.",
      );
    }
  };

  const askDeleteVendorSku = function (vendorSku) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteVendorSku(vendorSku.id)}
          message={`Are you sure you want to delete vendor SKU ${vendorSku.vendor_sku || vendorSku.id}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  return {
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    emptyModal,
    inventoryTypeOptions,
    getInventoryTypes,
    classTypeOptions,
    categoryOptions,
    boxTypeOptions,
    allergenOptions,
    warehouseOptions,
    warehouseLocationsOptions,
    getClassTypes,
    getCategoryTypes,
    getBoxTypes,
    getAllergens,
    getWarehouses,
    getWarehouseLocations,
    updateProduct,
    product,
    loading,
    getProductDetails,
    graphicsOptions,
    getGraphics,
    onDuplicate,
    getProductColours,
    getProductOdours,
    getProductTastes,
    productColours,
    productOdours,
    productTastes,
    vendorSKUs,
    vendorSKUsLoading,
    getProductVendorSkus,
    supplierOptions,
    getSuppliersForDropdown,
    createVendorSku,
    createVendorSkuInProgress,
    updateVendorSku,
    updateVendorSkuInProgress,
    deleteVendorSku,
    askDeleteVendorSku,
    productDuplicates,
    getProductDuplicates,
    updateProductDuplicate,
  };
}
